import { ROLE_DEFINITIONS } from "./roles";
import type { ModuleKey } from "../modules/registry";
import { MODULE_VIEW_VISIBILITY, MODULE_VISIBILITY, type ModuleViewDefinition } from "./visibility";
import type { LegacyAccessFlag, Permission, Role, User, UserStatus } from "./types";
import { resolveEffectivePermissions } from "./effectiveAccess";

export type AccessRule = {
  roles?: Role[];
  anyPermissions?: Permission[];
  allPermissions?: Permission[];
  anyPermissionPrefixes?: string[];
};

const HOTEL_SCOPED_PERMISSION_PREFIXES = [
  "pms.",
  "pos.",
  "ems.",
  "wms.",
  "revenue.",
  "voip.",
  "guests.",
  "cctv.",
  "keycoder.",
  "garage.",
];

export function normalizeUserStatus(status?: User["status"]): UserStatus {
  if (status === "disabled") return "disabled";
  if (status === "inactive" || status === "locked") return "inactive";
  return "active";
}

export function isUserActive(user: Pick<User, "status"> | null | undefined) {
  return normalizeUserStatus(user?.status) === "active";
}

export function isUserDisabled(user: Pick<User, "status"> | null | undefined) {
  return normalizeUserStatus(user?.status) === "disabled";
}

export function canDeleteUser(user: Pick<User, "status"> | null | undefined) {
  return isUserDisabled(user);
}

export function getRoleDefinition(role?: Role) {
  return ROLE_DEFINITIONS[role ?? "user"] ?? ROLE_DEFINITIONS.user;
}

export function getRolePermissions(role?: Role): Permission[] {
  if (!role) return [];
  return getRoleDefinition(role).permissions;
}

export function getLegacyFlagsForRole(role?: Role): LegacyAccessFlag[] {
  if (!role) return [];
  return getRoleDefinition(role).legacyFlags;
}

type GlobalAdminUser = Pick<User, "fullAccess" | "globalAdmin" | "platformRole">;

export function getUserPermissions(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole"> | null | undefined): Permission[] {
  return resolveEffectivePermissions(user);
}

export function hasPermission(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole"> | null | undefined, permission: Permission) {
  return getUserPermissions(user).includes(permission);
}

export function hasRole(user: Pick<User, "role" | "assignedRoles"> | null | undefined, role: Role) {
  return user?.role === role || user?.assignedRoles?.includes(role) || false;
}

export function canAccessRule(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole"> | null | undefined, rule?: AccessRule) {
  if (!rule) return true;
  if (isGlobalAdminUser(user)) return true;

  const userPermissions = getUserPermissions(user);
  const checks: boolean[] = [];
  if (rule.roles?.length) {
    checks.push(rule.roles.some((role) => hasRole(user, role)));
  }
  if (rule.anyPermissions?.length) {
    checks.push(rule.anyPermissions.some((permission) => userPermissions.includes(permission)));
  }
  if (rule.allPermissions?.length) {
    checks.push(rule.allPermissions.every((permission) => userPermissions.includes(permission)));
  }
  if (rule.anyPermissionPrefixes?.length) {
    checks.push(userPermissions.some((permission) =>
      rule.anyPermissionPrefixes?.some((prefix) => permission.startsWith(prefix))
    ));
  }

  return checks.length === 0 ? true : checks.some(Boolean);
}

// ── Per-hotel scoped access ─────────────────────────────────────────────────
// The backend enforces permissions per hotel; these helpers let the UI hide
// actions the API would reject at the currently selected hotel. Roles are still
// evaluated globally (a role is a user-level attribute); only permission checks
// are scoped to the hotel's granted set.

/** Expanded permissions the user holds AT a specific hotel. Admins keep the full
 *  set. Once a per-hotel breakdown is loaded, a missing/empty hotel entry means
 *  no hotel-scoped grants there; do not fall back to another hotel's union. */
export function permissionsForHotel(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole" | "propertyPermissions"> | null | undefined,
  hotelId: string | null | undefined,
): Set<Permission> {
  if (!user) return new Set();
  if (isGlobalAdminUser(user)) return new Set(getUserPermissions(user));
  const breakdown = user.propertyPermissions;
  if (!hotelId || !Array.isArray(breakdown)) return new Set(getUserPermissions(user));
  const entry = breakdown.find((p) => String(p.hotelId) === String(hotelId));
  if (!entry) return new Set();
  return new Set(entry.permissions ?? []);
}

export function isHotelScopedPermission(permission: Permission) {
  return HOTEL_SCOPED_PERMISSION_PREFIXES.some((prefix) => permission.startsWith(prefix));
}

export function hasPermissionAtHotel(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole" | "propertyPermissions"> | null | undefined,
  permission: Permission,
  hotelId: string | null | undefined,
) {
  if (!isHotelScopedPermission(permission)) return hasPermission(user, permission);
  return permissionsForHotel(user, hotelId).has(permission);
}

/** Like canAccessRule but evaluates anyPermissions/allPermissions against the
 *  permissions granted at `hotelId`. Roles remain global. Pass a null hotelId to
 *  fall back to the global evaluation. */
export function canAccessRuleAtHotel(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole" | "propertyPermissions"> | null | undefined,
  rule: AccessRule | undefined,
  hotelId: string | null | undefined,
) {
  if (!rule) return true;
  if (!hotelId) return canAccessRule(user, rule);
  if (isGlobalAdminUser(user)) return true;

  const hotelPerms = permissionsForHotel(user, hotelId);
  const checks: boolean[] = [];
  const hasScopedBreakdown = Array.isArray(user?.propertyPermissions);
  if (!hasScopedBreakdown && rule.roles?.length) {
    checks.push(rule.roles.some((role) => hasRole(user, role)));
  }
  if (rule.anyPermissions?.length) {
    checks.push(rule.anyPermissions.some((permission) => hotelPerms.has(permission)));
  }
  if (rule.allPermissions?.length) {
    checks.push(rule.allPermissions.every((permission) => hotelPerms.has(permission)));
  }
  if (rule.anyPermissionPrefixes?.length) {
    checks.push([...hotelPerms].some((permission) =>
      rule.anyPermissionPrefixes?.some((prefix) => permission.startsWith(prefix))
    ));
  }

  return checks.length === 0 ? true : checks.some(Boolean);
}

export function hasAnyPermission(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole"> | null | undefined, permissions: Permission[]) {
  const userPermissions = new Set(getUserPermissions(user));
  return permissions.some((permission) => userPermissions.has(permission));
}

export function hasAllPermissions(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole"> | null | undefined, permissions: Permission[]) {
  const userPermissions = new Set(getUserPermissions(user));
  return permissions.every((permission) => userPermissions.has(permission));
}

export function isAdminUser(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole"> | null | undefined) {
  return hasPermission(user, "admin.access");
}

export function isGlobalAdminUser(user: GlobalAdminUser | null | undefined) {
  return Boolean(
    user?.globalAdmin
    || user?.fullAccess
    || user?.platformRole === "global_admin"
  );
}

export function canAccessModule(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole"> | null | undefined, moduleKey: ModuleKey) {
  return canAccessRule(user, MODULE_VISIBILITY[moduleKey]);
}

export function canAccessModuleAtHotel(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole" | "propertyPermissions"> | null | undefined,
  moduleKey: ModuleKey,
  hotelId: string | null | undefined,
) {
  return canAccessRuleAtHotel(user, MODULE_VISIBILITY[moduleKey], hotelId);
}

export function canEnterModuleAtHotel(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole" | "propertyPermissions"> | null | undefined,
  moduleKey: ModuleKey,
  hotelId: string | null | undefined,
) {
  return canAccessModuleAtHotel(user, moduleKey, hotelId)
    || getVisibleModuleViewsAtHotel(user, moduleKey, hotelId).length > 0;
}

export function getModuleViewDefinition(moduleKey: ModuleKey, viewKey: string): ModuleViewDefinition | undefined {
  return MODULE_VIEW_VISIBILITY[moduleKey]?.find((view) => view.key === viewKey);
}

export function canAccessModuleView(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole"> | null | undefined,
  moduleKey: ModuleKey,
  viewKey: string,
) {
  const view = getModuleViewDefinition(moduleKey, viewKey);
  return Boolean(view && canAccessRule(user, view.access));
}

export function canAccessModuleViewAtHotel(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole" | "propertyPermissions"> | null | undefined,
  moduleKey: ModuleKey,
  viewKey: string,
  hotelId: string | null | undefined,
) {
  const view = getModuleViewDefinition(moduleKey, viewKey);
  return Boolean(view && canAccessRuleAtHotel(user, view.access, hotelId));
}

export function getVisibleModuleViewsAtHotel(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole" | "propertyPermissions"> | null | undefined,
  moduleKey: ModuleKey,
  hotelId: string | null | undefined,
) {
  return (MODULE_VIEW_VISIBILITY[moduleKey] ?? []).filter((view) => canAccessRuleAtHotel(user, view.access, hotelId));
}

export function getDefaultModuleViewAtHotel(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "globalAdmin" | "platformRole" | "propertyPermissions"> | null | undefined,
  moduleKey: ModuleKey,
  hotelId: string | null | undefined,
) {
  return getVisibleModuleViewsAtHotel(user, moduleKey, hotelId)[0];
}
